import React, { useState } from 'react';
import {
  Flame,
  CheckCircle,
  RotateCcw,
  Snowflake,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Brand tokens for Settings / mobile consistency
const DEEP_TEAL = '#006D77';
const SAGE = '#83C5BE';
const TEXT_HEADING = '#1E1E1E';
const TEXT_DESC = '#636E72';
const BORDER = '#E1E4E8';

const StreakManager = ({ streakData }) => {
    const { t } = useTranslation();
  const data = streakData || {
    currentStreak: 0,
    longestStreak: 0,
    freezesAvailable: 2,
    repairsAvailable: 1,
    weekendExcluded: false,
    streakHistory: []
  };

  // Calculate next milestone
  const getNextMilestone = (current) => {
    const milestones = [3, 7, 14, 30, 100, 365];
    const next = milestones.find(m => m > current);
    return next || 365;
  };

  const nextMilestone = getNextMilestone(data.currentStreak);
  const milestoneProgress = (data.currentStreak / nextMilestone) * 100;

  // Get milestone info
  const getMilestoneInfo = (streak) => {
  if (streak >= 365) return {
    name: t('gamification.streakManager.milestones.yearLegend'),
    color: 'from-yellow-400 to-orange-500',
    icon: '🏆'
  };
  if (streak >= 100) return {
    name: t('gamification.streakManager.milestones.centuryClub'),
    color: 'from-purple-400 to-pink-500',
    icon: '💎'
  };
  if (streak >= 30) return {
    name: t('gamification.streakManager.milestones.monthlyMaster'),
    color: 'from-blue-400 to-purple-500',
    icon: '🏅'
  };
  if (streak >= 14) return {
    name: t('gamification.streakManager.milestones.twoWeekChampion'),
    color: 'from-green-400 to-teal-500',
    icon: '⚡'
  };
  if (streak >= 7) return {
    name: t('gamification.streakManager.milestones.weekWarrior'),
    color: 'from-orange-400 to-red-500',
    icon: '🎯'
  };
  if (streak >= 3) return {
    name: t('gamification.streakManager.milestones.gettingStarted'),
    color: 'from-blue-400 to-indigo-500',
    icon: '🚀'
  };
  return {
    name: t('gamification.streakManager.milestones.startYourJourney'),
    color: 'from-gray-400 to-gray-500',
    icon: '🌱'
  };
};

  const currentMilestone = getMilestoneInfo(data.currentStreak);

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-6 border border-[#E1E4E8] shadow-sm">

      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg font-bold flex items-center gap-2" style={{ color: TEXT_HEADING }}>
          <Flame className="w-5 h-5 flex-shrink-0" style={{ color: DEEP_TEAL }} />
          {t('gamification.streakManager.title')}
        </h3>
      </div>

      {/* Current Streak Display, compact on mobile */}
      <div className="rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6 border" style={{ backgroundColor: 'rgba(0, 109, 119, 0.08)', borderColor: BORDER }}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-3xl sm:text-4xl md:text-5xl font-black" style={{ color: TEXT_HEADING }}>
                {data.currentStreak}
              </span>
              <span className="text-lg sm:text-xl font-semibold" style={{ color: TEXT_DESC }}>
                {t('gamification.streakManager.currentStreak.days')}
              </span>
            </div>
            <div className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${currentMilestone.color} text-white text-xs sm:text-sm font-bold mt-2`}>
              {currentMilestone.icon} {currentMilestone.name}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs sm:text-sm mb-0.5" style={{ color: TEXT_DESC }}>
              {t('gamification.streakManager.currentStreak.longestStreak')}
            </div>
            <div className="text-2xl sm:text-3xl font-bold" style={{ color: TEXT_HEADING }}>
              {data.longestStreak}
            </div>
          </div>
        </div>

        {/* Progress to Next Milestone, brand teal */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs sm:text-sm" style={{ color: TEXT_DESC }}>
            <span>{t('gamification.streakManager.progress.next')} {getMilestoneInfo(nextMilestone).name}</span>
            <span>
              {t('gamification.streakManager.progress.currentOfTotal', {
                current: data.currentStreak,
                total: nextMilestone
              })}
            </span>
          </div>
          <div className="h-2.5 sm:h-3 rounded-full overflow-hidden bg-white border border-[#E1E4E8]">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${milestoneProgress}%`, backgroundColor: DEEP_TEAL }}
            />
          </div>
        </div>
      </div>

      {/* Streak Protection Tools, single column on mobile, 48px touch-friendly */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="rounded-2xl p-4 border min-h-[48px] flex flex-col justify-center" style={{ backgroundColor: 'rgba(0, 109, 119, 0.12)', borderColor: BORDER }}>
          <div className="flex items-center gap-2 mb-2">
            <Snowflake className="w-5 h-5 flex-shrink-0" style={{ color: DEEP_TEAL }} />
            <h4 className="font-bold text-sm sm:text-base" style={{ color: TEXT_HEADING }}>
              {t('gamification.streakManager.freeze.title')}
            </h4>
          </div>
          <div className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: DEEP_TEAL }}>
            {data.freezesAvailable}
          </div>
          <p className="text-xs" style={{ color: TEXT_DESC }}>
            {t('gamification.streakManager.freeze.description')}
          </p>
          <div className="flex gap-1 mt-2">
            {[...Array(2)].map((_, i) => (
              <div
                key={i}
                className={`flex-1 h-1.5 rounded-full ${i < data.freezesAvailable ? '' : 'bg-gray-300'}`}
                style={i < data.freezesAvailable ? { backgroundColor: DEEP_TEAL } : {}}
              />
            ))}
          </div>
        </div>

        <div className="rounded-2xl p-4 border min-h-[48px] flex flex-col justify-center" style={{ backgroundColor: 'rgba(131, 197, 190, 0.18)', borderColor: BORDER }}>
          <div className="flex items-center gap-2 mb-2">
            <RotateCcw className="w-5 h-5 flex-shrink-0" style={{ color: SAGE }} />
            <h4 className="font-bold text-sm sm:text-base" style={{ color: TEXT_HEADING }}>
              {t('gamification.streakManager.repair.title')}
            </h4>
          </div>
          <div className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: SAGE }}>
            {data.repairsAvailable}
          </div>
          <p className="text-xs" style={{ color: TEXT_DESC }}>
            {t('gamification.streakManager.repair.description')}
          </p>
          <div className="flex gap-1 mt-2">
            {[...Array(1)].map((_, i) => (
              <div
                key={i}
                className={`flex-1 h-1.5 rounded-full ${i < data.repairsAvailable ? '' : 'bg-gray-300'}`}
                style={i < data.repairsAvailable ? { backgroundColor: SAGE } : {}}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Streak Calendar (last 7 days), smaller cells on mobile */}
      <div className="pt-4 sm:pt-6 border-t" style={{ borderColor: BORDER }}>
        <h4 className="font-bold text-sm mb-3" style={{ color: TEXT_HEADING }}>
          {t('gamification.streakManager.calendar.title')}
        </h4>
        <div className="flex gap-1.5 sm:gap-2">
          {(data.streakHistory?.slice(-7) || []).map((day, i) => (
            <div
              key={i}
              className={`flex-1 min-w-0 min-h-[44px] sm:h-12 rounded-lg flex items-center justify-center touch-manipulation ${
                day.completed
                  ? 'text-white'
                  : day.freezeUsed
                  ? 'text-white'
                  : 'bg-[#F0F2F4] text-gray-400'
              }`}
              style={
                day.completed
                  ? { backgroundColor: SAGE }
                  : day.freezeUsed
                  ? { backgroundColor: DEEP_TEAL }
                  : {}
              }
            >
              {day.completed ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" /> :
               day.freezeUsed ? <Snowflake className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" /> :
               <span className="text-[10px] sm:text-xs font-bold truncate">{day.day}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SettingToggle = ({ label, description, enabled }) => {
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
      <div className="flex-1">
        <div className="font-semibold text-gray-900 text-sm">{label}</div>
        <div className="text-xs text-gray-600">{description}</div>
      </div>
      <div className={`w-12 h-6 rounded-full transition-colors ${
        enabled ? 'bg-blue-500' : 'bg-gray-300'
      }`}>
        <div className={`w-5 h-5 bg-white rounded-full mt-0.5 transition-transform ${
          enabled ? 'ml-6' : 'ml-0.5'
        }`} />
      </div>
    </div>
  );
};

export default StreakManager;